import React, { useState, useRef } from 'react';
import './SearchBar.css';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSetCode?: (code: string) => void;
  placeholder?: string;
}

const SEARCH_HINTS = [
  { prefix: '',    example: 'lightning bolt',     desc: 'Search by card name' },
  { prefix: 'o:',  example: 'o:flying',           desc: 'Search oracle / rules text' },
  { prefix: 'f:',  example: 'f:defeated',         desc: 'Search flavour text' },
  { prefix: 'a:',  example: 'a:terese nielsen',   desc: 'Search by artist name' },
  { prefix: 's:',  example: 's:blb',              desc: 'Set filter — type code then Enter to add as badge' },
];

export default function SearchBar({ value, onChange, onSetCode, placeholder }: Props) {
  const [showHints, setShowHints] = useState(false);
  const inputRef  = useRef<HTMLInputElement>(null);
  const wrapRef   = useRef<HTMLDivElement>(null);

  // Dismiss on Escape or Enter key
  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') setShowHints(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  // Dismiss on click outside the search bar
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowHints(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Determine active prefix for highlighting
  // s: is excluded — it converts to a badge immediately and never stays in the bar
  const activePrefix = value.startsWith('o:') ? 'o:'
                     : value.startsWith('f:') ? 'f:'
                     : value.startsWith('a:') ? 'a:'
                     : '';

  const prefixLabel = activePrefix === 'o:' ? 'ORACLE'
                    : activePrefix === 'f:' ? 'FLAVOUR'
                    : activePrefix === 'a:' ? 'ARTIST'
                    : null;

  return (
    <div className="search-bar-wrap" ref={wrapRef}>
      <div className={`search-bar ${showHints ? 'focused' : ''}`}>
        <div className="search-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
        </div>
        {prefixLabel && (
          <span className="search-prefix-badge">{prefixLabel}</span>
        )}
        <input
          ref={inputRef}
          className="search-input"
          type="text"
          placeholder={placeholder || 'Quick Search — name, o: oracle, f: flavour, a: artist, s: set'}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            // Convert s:xxx to a set badge when user presses Enter or Space
            if ((e.key === 'Enter' || e.key === ' ') && value.toLowerCase().startsWith('s:')) {
              const code = value.slice(2).trim().toLowerCase();
              if (code.length > 0 && onSetCode) {
                e.preventDefault();
                onSetCode(code);
                setShowHints(false);
              }
            }
            if (e.key === 'Escape' || e.key === 'Enter') setShowHints(false);
          }}
          onFocus={() => setShowHints(true)}
          onBlur={() => {}}
          spellCheck={false}
        />
        <button
          className="search-help-btn"
          onMouseDown={e => { e.preventDefault(); setShowHints(!showHints); }}
          title="Search syntax help"
        >
          ?
        </button>
        {value && (
          <button className="search-clear"
            onClick={() => { onChange(''); inputRef.current?.focus(); }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        )}
      </div>

      {showHints && (
        <div className="search-hints">
          <div className="hints-title">Quick Search</div>
          {SEARCH_HINTS.map(h => (
            <button
              key={h.prefix + h.example}
              className="hint-row"
              onMouseDown={e => {
                e.preventDefault();
                onChange(h.prefix);
                inputRef.current?.focus();
              }}
            >
              <span className="hint-example">{h.example}</span>
              <span className="hint-desc">{h.desc}</span>
            </button>
          ))}
          <div className="hints-tip">
            Click an example to start, or just type your search above
          </div>
        </div>
      )}
    </div>
  );
}
